import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ReportDefinition, ReportResult } from '../../core/models/report.model';
import { ConfirmsUnsavedChanges } from '../../core/guards/unsaved-changes.guard';
import { definitionsEqual } from '../../core/reports/report-definition.utils';
import { CriteriaResolvers, ReportQueryService } from '../../core/reports/report-query.service';
import { ReportSessionService } from '../../core/reports/report-session.service';
import { SavedReportService } from '../../core/reports/saved-report.service';
import { FormService } from '../../core/services/form.service';
import { LoggerService } from '../../core/services/logger.service';
import { ModalConfig, ModalService } from '../../core/services/modal.service';
import { PermissionService } from '../../core/services/permission.service';
import { ToastService } from '../../core/services/toast.service';
import { TranslationService } from '../../core/services/translation.service';
import { exportToCSV, exportToXLSX } from '../../shared/utils/export.utils';
import { buildReportCriteriaSummary, ReportCriteriaSummary } from './report-criteria-summary';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardActionsComponent } from '../../shared/components/card/card-actions.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { DiscardChangesDialogComponent } from './discard-changes-dialog/discard-changes-dialog.component';
import {
  SaveReportAsDialogComponent,
  SaveReportAsModel,
} from './save-report-as-dialog/save-report-as-dialog.component';

/**
 * Report Results view (Phase 1).
 *
 * Runs a report definition against the local replica and renders a summary
 * bar + results table with CSV/XLSX export and print support.
 *
 * For a saved report (route `/reports/:id/results`), the definition is
 * resolved from an in-progress {@link ReportSessionService} draft if one
 * matches the id (e.g. unsaved criteria changes carried over from the
 * builder), otherwise loaded fresh from {@link SavedReportService} — which is
 * what makes the URL refreshable and shareable. For an ad-hoc report (route
 * `/reports/run`, no id), only the session has the definition, so refreshing
 * redirects to the builder since there's nothing durable to reload.
 */
@Component({
  selector: 'app-report-results',
  standalone: true,
  imports: [
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    CardActionsComponent,
    IconComponent,
    ButtonComponent,
    SpinnerComponent,
    HasPermissionDirective,
    TranslatePipe,
  ],
  templateUrl: './report-results.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './report-results.component.css',
})
export class ReportResultsComponent implements OnInit, ConfirmsUnsavedChanges {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private session = inject(ReportSessionService);
  private queryService = inject(ReportQueryService);
  private savedReports = inject(SavedReportService);
  private formService = inject(FormService);
  private permissions = inject(PermissionService);
  private toast = inject(ToastService);
  private modalService = inject(ModalService);
  private translate = inject(TranslationService);
  private log = inject(LoggerService).getLogger('report-results');

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly result = signal<ReportResult | null>(null);

  private readonly definition = signal<ReportDefinition | null>(null);
  private readonly existingReportNames = signal<string[]>([]);
  private readonly formName = signal<string | undefined>(undefined);
  private readonly criteriaResolvers = signal<CriteriaResolvers | null>(null);
  /** The persisted version of the current definition (null if never saved),
   *  used to detect unsaved changes — see {@link isDirty}. */
  private baseline: ReportDefinition | null = null;
  /** Set immediately before a deliberate navigation (Edit report, Save, Save
   *  As) so {@link confirmDiscardIfDirty} lets it through without prompting. */
  private bypassUnsavedChangesGuard = false;

  readonly reportName = computed(() => this.definition()?.name ?? '');
  readonly hasRows = computed(() => (this.result()?.rows.length ?? 0) > 0);
  readonly isAggregate = computed(() => {
    const def = this.definition();
    return !!def && def.aggregation.mode !== 'none';
  });
  /** Whether this is an already-saved report, as opposed to an ad-hoc one. */
  readonly isSaved = computed(() => !!this.definition()?.id);

  /** Read-only summary of the criteria that produced the current results —
   *  only available once the definition and its label resolvers have loaded. */
  readonly criteria = computed<ReportCriteriaSummary | null>(() => {
    const def = this.definition();
    const resolvers = this.criteriaResolvers();
    if (!def || !resolvers) return null;
    return buildReportCriteriaSummary(def, {
      formName: this.formName(),
      resolvers,
      t: (key, params) => this.translate.translate(key, params),
    });
  });

  async ngOnInit(): Promise<void> {
    const id = this.route.snapshot.paramMap.get('id');
    const def = await this.resolveDefinition(id);
    if (!def) {
      this.router.navigate([id ? '/reports' : '/reports/new']);
      return;
    }
    this.definition.set(def);
    const [visible, saved, forms, resolvers] = await Promise.all([
      this.savedReports.getAllVisible(),
      def.id ? this.savedReports.getById(def.id) : Promise.resolve(null),
      this.formService.getAll(),
      this.queryService.loadCriteriaResolvers(def),
    ]);
    this.existingReportNames.set(visible.map((r) => r.name));
    this.baseline = saved?.definition ?? null;
    this.formName.set(forms.find((f) => f.id === def.formId)?.name);
    this.criteriaResolvers.set(resolvers);
    await this.runReport();
  }

  /** Resolves the definition to run: for a saved report (id present), prefers
   *  an in-progress session draft for that same id (unsaved criteria changes
   *  carried over from the builder) and otherwise loads it fresh — the fresh
   *  load is what makes a refresh or a shared link work. For an ad-hoc report
   *  (no id), only the session ever has the definition. */
  private async resolveDefinition(id: string | null): Promise<ReportDefinition | null> {
    const draft = this.session.current();
    if (!id) return draft;
    if (draft?.id === id) return draft;
    const saved = await this.savedReports.getById(id);
    return saved?.definition ?? null;
  }

  /** Whether the definition currently running differs from what's persisted
   *  (or, for an ad-hoc report, whether it's ever been saved at all). */
  isDirty(): boolean {
    return !definitionsEqual(this.definition(), this.baseline);
  }

  /** CanDeactivate hook (see `unsavedChangesGuard`): confirms with the user
   *  before leaving this route with unsaved criteria changes. */
  async confirmDiscardIfDirty(): Promise<boolean> {
    if (this.bypassUnsavedChangesGuard) {
      this.bypassUnsavedChangesGuard = false;
      return true;
    }
    if (!this.isDirty()) return true;
    return this.confirmDiscard();
  }

  private async confirmDiscard(): Promise<boolean> {
    const closeSignal = signal<{ action: string } | null>(null);
    const config: ModalConfig = {
      component: DiscardChangesDialogComponent,
      title: this.translate.translate('reports.discardChanges.title'),
      size: 'small',
      closeSignal,
      footerClassName: 'flex flex-wrap gap-md flex-responsive-xs col-2',
      actionButtons: [
        {
          id: 'cancel',
          label: this.translate.translate('button.cancel'),
          variant: 'secondary',
          onClick: async () => closeSignal.set({ action: 'cancel' }),
        },
        {
          id: 'discard',
          label: this.translate.translate('reports.discardChanges.leave'),
          className: 'ml-auto',
          variant: 'danger',
          onClick: async () => closeSignal.set({ action: 'discard' }),
        },
      ],
    };
    const result = await this.modalService.openModal(config);
    return result?.action === 'discard';
  }

  canSave(): boolean {
    return this.permissions.has('reports.create') && !!this.definition();
  }

  /** "Save As" is only offered once there's an existing saved report to branch
   *  from — for an ad-hoc (never-saved) report, plain Save already creates it. */
  canSaveAs(): boolean {
    return this.isSaved() && this.canSave();
  }

  /** Persists the currently-running report (updates it if already saved). */
  async save(): Promise<void> {
    const def = this.definition();
    if (!def || !this.canSave() || this.saving()) return;
    this.saving.set(true);
    try {
      if (def.id) {
        const updated = await this.savedReports.update(def.id, def);
        this.definition.set(updated.definition);
        this.baseline = updated.definition;
      } else {
        const created = await this.savedReports.create(def);
        this.definition.set(created.definition);
        this.baseline = created.definition;
        this.existingReportNames.update((names) => [...names, created.name]);
      }
      this.toast.success(this.translate.translate('reports.results.saved'));
    } catch (error) {
      this.log.error('Failed to save report from results view', error);
      this.toast.error(this.translate.translate('reports.results.saveError'));
    } finally {
      this.saving.set(false);
    }
  }

  /** Saves the current criteria as a brand-new report under a name the user
   *  confirms is unique, leaving the original saved report untouched. */
  async saveAs(): Promise<void> {
    const def = this.definition();
    if (!def || !this.canSaveAs() || this.saving()) return;

    const modelSignal = signal<SaveReportAsModel>({ name: def.name ?? '' });
    const saveDisabled = signal<string | null>(null);
    const closeSignal = signal<{ action: string } | null>(null);
    const config: ModalConfig = {
      component: SaveReportAsDialogComponent,
      componentInputs: {
        modelSignal,
        existingNames: this.existingReportNames(),
        saveDisabledSignal: saveDisabled,
      },
      title: this.translate.translate('reports.saveAsDialog.title'),
      size: 'small',
      closeSignal,
      footerClassName: 'flex flex-wrap gap-md flex-responsive-xs col-2',
      actionButtons: [
        {
          id: 'cancel',
          label: this.translate.translate('button.cancel'),
          variant: 'secondary',
          onClick: async () => closeSignal.set({ action: 'cancel' }),
        },
        {
          id: 'save',
          label: this.translate.translate('button.save'),
          className: 'ml-auto',
          variant: 'primary',
          disabled: saveDisabled as any,
          onClick: async () => closeSignal.set({ action: 'save' }),
        },
      ],
    };

    const result = await this.modalService.openModal(config);
    if (result?.action !== 'save') return;

    const name = modelSignal().name.trim();
    this.saving.set(true);
    try {
      const created = await this.savedReports.create({ ...def, id: undefined, name });
      this.definition.set(created.definition);
      this.baseline = created.definition;
      this.existingReportNames.update((names) => [...names, created.name]);
      this.toast.success(this.translate.translate('reports.results.saved'));
    } catch (error) {
      this.log.error('Failed to save report as a new copy', error);
      this.toast.error(this.translate.translate('reports.results.saveError'));
    } finally {
      this.saving.set(false);
    }
  }

  private async runReport(): Promise<void> {
    const def = this.definition();
    if (!def) return;
    this.loading.set(true);
    this.error.set(null);
    try {
      const result = await this.queryService.execute(def);
      this.result.set(result);
    } catch (err) {
      this.log.error('Report execution failed', err);
      this.error.set(this.translate.translate('reports.results.error'));
    } finally {
      this.loading.set(false);
    }
  }

  editReport(): void {
    const def = this.definition();
    // Keep the current definition in the session so the builder can rehydrate
    // it (including any unsaved changes — see ReportBuilderComponent.loadSavedReport).
    if (def) this.session.set(def);
    this.bypassUnsavedChangesGuard = true;
    if (def?.id) {
      this.router.navigate(['/reports', def.id]);
    } else {
      this.router.navigate(['/reports/new']);
    }
  }

  back(): void {
    this.router.navigate(['/reports']);
  }

  print(): void {
    window.print();
  }

  exportCsv(): void {
    this.export((rows, name) => exportToCSV(rows, name), 'csv');
  }

  exportXlsx(): void {
    this.export((rows, name) => exportToXLSX(rows, name), 'xlsx');
  }

  private export(fn: (rows: Record<string, unknown>[], name: string) => void, kind: string): void {
    const result = this.result();
    if (!result || result.rows.length === 0) {
      this.toast.error(this.translate.translate('reports.results.exportEmpty'));
      return;
    }
    try {
      const rows = this.toExportRows(result);
      const filename = this.sanitizeFilename(this.reportName() || 'report');
      fn(rows, filename);
    } catch (err) {
      this.log.error(`Report ${kind} export failed`, err);
      this.toast.error(this.translate.translate('reports.results.exportError'));
    }
  }

  /** Maps result rows (keyed c0…) into label-keyed objects for export. */
  private toExportRows(result: ReportResult): Record<string, unknown>[] {
    return result.rows.map((row) => {
      const out: Record<string, unknown> = {};
      for (const col of result.columns) {
        out[col.label] = row[col.key];
      }
      return out;
    });
  }

  private sanitizeFilename(name: string): string {
    return name.replace(/[^\w\-]+/g, '_').replace(/^_+|_+$/g, '') || 'report';
  }
}
